import type { ReactNode } from 'react';

import { BAR_HEIGHT } from './barScale';

export interface CompositionSegment {
  key: string;
  /** The word beside the figure. A colour never travels alone. */
  label: string;
  /** A background utility resolving to a semantic token. Never a colour value. */
  fill: string;
  /** The quantity, used for the proportion. */
  value: number;
  /**
   * How the quantity is written, where the raw number is not what a reader wants.
   * Board states count tasks and print the number; token categories count tokens and
   * print `1.2M`. Defaults to the value itself.
   */
  display?: string;
  /**
   * Makes the legend row a link (spec 153 FR-011): the caller's own link around
   * the row's content, so this component names no route. The usage page passes
   * none, and its legend stays a picture's caption.
   */
  link?: (content: ReactNode) => ReactNode;
}

export interface CompositionBarProps {
  /**
   * The parts, in the order they should be drawn.
   *
   * **Every segment given is legended; only the non-zero ones are drawn.** A
   * zero-width band is meaningless in the bar and meaningful in the legend, and
   * which zeros deserve a legend row is a question only the caller can answer:
   * the overview omits a board state nobody is in, while the usage page keeps a
   * token category at zero because "no caching happened" and "this build does
   * not report caching" must not look alike (spec 076 FR-010).
   *
   * So there is no prop for it. The caller passes what it wants legended, which
   * puts the decision where its reasoning already lives.
   */
  segments: CompositionSegment[];
  /** The whole, for proportions. Nothing is drawn when this is zero. */
  total: number;
  /** The figure as a sentence, for anyone who cannot see it. */
  description: string;
  /**
   * Whether the bar labels its own segments (the default). Off only where the caller
   * lays every segment's label and figure out itself, beside the bar (spec 219's token
   * cells) — the rule that a colour never travels alone still holds there.
   */
  legend?: boolean;
}

/**
 * A whole, divided into named parts.
 *
 * Extracted from `WorkspaceCounters` by spec 076, where it was private and hard-wired to
 * four board states. Two concrete callers now exist — board states and token categories —
 * which is the threshold rule 2.4 sets for introducing a seam, and the extraction is
 * shaped so that neither caller configures the other's behaviour.
 *
 * A stacked bar rather than a pie, and the reason is the same both times it has come up:
 * angle is the least accurate perceptual channel there is, and the parts a reader most
 * needs to compare are exactly the ones a pie makes hardest. Lengths on a shared baseline
 * are what a person can actually read.
 */
export function CompositionBar({
  segments,
  total,
  description,
  legend = true,
}: CompositionBarProps) {
  if (total === 0) return null;

  const drawn = segments.filter((segment) => segment.value > 0);
  const linked = segments.some((segment) => segment.link !== undefined);

  return (
    <div className="flex flex-col gap-2">
      {/*
        `gap-0.5` between segments rather than a border around each: a border adds a
        further colour to the figure, and at this height it is thicker than several of
        the segments it would be separating. The gap lets the surface behind show
        through, which separates two fills without drawing anything.
      */}
      <div
        role="img"
        aria-label={description}
        className={`flex ${BAR_HEIGHT} gap-0.5`}
      >
        {drawn.map((segment) => (
          <span
            key={segment.key}
            className={`${segment.fill} first:rounded-s-full last:rounded-e-full`}
            style={{ width: `${String((segment.value / total) * 100)}%` }}
          />
        ))}
      </div>

      {/*
        Every segment direct-labelled, always. A status colour never travels alone — it
        is the rule the palette's own margins make non-negotiable, and it is what these
        surfaces were already doing in prose.
      */}
      {/*
        Hidden from assistive technology while it only repeats the description;
        once its rows are links they are the panel's controls, and are read.
      */}
      {legend && (
        <ul
          aria-hidden={linked ? undefined : true}
          className="text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 text-xs"
        >
          {/*
            Figure and word in one text node, not two elements. Split, the word alone
            becomes a second element reading exactly "running" — indistinguishable from a
            counter elsewhere on the page to anything matching on text, a screen reader and
            a test alike.
          */}
          {segments.map((segment) => {
            const content = (
              <>
                <span
                  aria-hidden="true"
                  className={`size-2 shrink-0 rounded-full ${segment.fill}`}
                />
                <span className="tabular-nums">
                  {`${segment.display ?? String(segment.value)} ${segment.label}`}
                </span>
              </>
            );
            return (
              <li key={segment.key} className="flex items-center gap-1.5">
                {segment.link ? segment.link(content) : content}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
